import React from 'react';
import styles from './step1.module.scss';
import Select from 'components/form/select/select';
import * as Yup from 'yup';
import FormBuilder from 'components/form/builder/builder';
import * as Constants from '../constants';
import DatePicker from 'components/form/date/date';

const schemaShape = {
  // Schema items ordered as they appear on web form
  AffiliateId: Yup.number().required('Affiliation Required'),
  LicenseNo: Yup.string()
    .trim()
    .matches(/^[0-9A-Za-z]{1,10}$/, {
      message:
        'Invalid license number.  Should be less than 10 characters. Only letters and numbers allowed.'
    })
    .required('License Number Required'),
  LicenseExpDate: Yup.date()
    .typeError('Invalid Date')
    .required('Required'),
  AgencyName: Yup.string()
    .trim()
    .max(
      Constants.MAX_CHARS_FOR_FIELD,
      `Please enter less than ${Constants.MAX_CHARS_FOR_FIELD} characters!`
    )
    .required('Entity Name on License Required'),
  FederalTaxId: Yup.string()
    .trim()
    .matches(Constants.DIGITS_ONLY, {
      message: 'FEIN should consist only of digits (0-9).'
    })
    .matches(/^[0-9-]*[0-9]$/, {
      message: 'FEIN/TIN should contain at least 9 digits.'
    })
    .required('Federal Tax ID Required'), // Limit this to 9 digits
  TaxIDName: Yup.string()
    .trim()
    .matches(/^[0-9A-Za-z\s]*$/, {
      message: 'Only letters, numbers, and spaces allowed.'
    })
    .max(
      Constants.MAX_CHARS_FOR_FIELD,
      `Please enter less than ${Constants.MAX_CHARS_FOR_FIELD} characters!`
    )
    .required('Tax ID Name Required'),
  BusinessType: Yup.string().required('Structure of Entity Required'),
  PrincipalName: Yup.string()
    .trim()
    .matches(/^[A-Za-z\s]*$/, { message: 'Only letters and spaces allowed.' })
    .max(
      Constants.MAX_CHARS_FOR_FIELD,
      `Please enter less than ${Constants.MAX_CHARS_FOR_FIELD} characters!`
    )
    .required('Name of Agency Owner/Principal Required'),
  PrincipalEmail: Yup.string()
    .email('Invalid Email')
    .max(
      Constants.MAX_CHARS_FOR_FIELD,
      `Please enter less than ${Constants.MAX_CHARS_FOR_FIELD} characters!`
    )
    .required('Email Address Required')
};

const getAffiliate = (affiliateOptions) => {
  let affiliate = '';
  const
    affiliateInSession = (sessionStorage.getItem("affiliate") || '').toLowerCase(),
    matchedAffiliateField = affiliateOptions
      .filter(x => x.systemFieldName && x.systemFieldName.toLowerCase().indexOf(affiliateInSession) > -1),
    matchedAffiliateName = affiliateOptions
      .filter(x => x.name.toLowerCase().replace(/\s/g, '').indexOf(affiliateInSession) > -1),
    foundMatch = matchedAffiliateField.length + matchedAffiliateName.length;

  if (affiliateInSession && foundMatch) {
    if (matchedAffiliateField.length > 0) {
      affiliate = matchedAffiliateField[0].value;
    }
    else {
      affiliate = matchedAffiliateName[0].value;
    }
  }

  return affiliate;
}

const getAffiliateOptions = (props) => {
  const affiliates = [...props.formDefinitions.affiliates];
  if (!getAffiliate(props.formDefinitions.affiliates)) {
    affiliates.unshift({ name: ' - Not Affiliated', value: 0 });
  }

  return affiliates;
}

const Step1 = props => {

  const elements = [
    {
      Component: Select,
      props: {
        options: getAffiliateOptions(props),
        placeholder: 'Select One'
      },
      label: 'Are you affiliated with any of the following?',
      name: 'AffiliateId'
    },
    {
      label: 'What is the entity license number?',
      placeholder: 'Enter License Number',
      name: 'LicenseNo',
      mapped: true,
      maxLength: 20
    },
    {
      Component: DatePicker,
      label: 'What is the expiration date of the license?',
      placeholder: 'Select Date',
      name: 'LicenseExpDate'
    },
    {
      label: 'What is the entity name on license?',
      placeholder: 'Enter Name',
      name: 'AgencyName',
      maxLength: 255
    },
    {
      label: 'What is your Tax ID number (FEIN/TIN/SSN)?',
      placeholder: 'Enter FEIN/TIN',
      name: 'FederalTaxId',
      maxLength: 20
    },
    {
      label: 'Tax ID Business Name',
      placeholder: 'Enter Tax ID Business Name',
      name: 'TaxIDName',
      maxLength: 128
    },
    {
      Component: Select,
      props: {
        options: [
          {
            name: 'Sole Proprietor or Individual',
            value: 'Sole Proprietor or Individual'
          },
          { name: 'Corporation', value: 'Corporation' },
          {
            name: 'Limited Liability Company',
            value: 'Limited Liability Company'
          },
          { name: 'Partnership', value: 'Partnership' },
          { name: 'Limited Partnership', value: 'Limited Partnership' },
          { name: 'Joint Venture', value: 'Joint Venture' }
        ],
        placeholder: 'Select One'
      },
      label: 'What is the structure of the entity?',
      placeholder: 'Select One',
      name: 'BusinessType'
    },
    {
      label: 'What is the name of the agency owner/principal?',
      name: 'PrincipalName',
      placeholder: 'Enter Name',
      maxLength: 255
    },
    {
      label: 'What is the email address of the agency owner/principal?',
      placeholder: 'Enter email',
      name: 'PrincipalEmail',
      maxLength: 255
    }
  ];

  const affiliateValue = getAffiliate(props.formDefinitions.affiliates);

  const initialValues = {
    AffiliateId: affiliateValue,
    AgencyName: props.response?.result?.entityinfo?.AgencyInfo?.AgencyName || '',
    TaxIDName: props.response?.result?.entityinfo?.AgencyInfo?.TaxIDName || '',
    LicenseNo: props.response?.result?.entityinfo?.AgencyInfo?.EntityLicenseNumber || '',
    LicenseExpDate: '',
    BusinessType: '',
    PrincipalName: props.response?.result?.entityinfo?.AgencyInfo?.AgencyOwner || '',
    PrincipalEmail: props.response?.result?.entityinfo?.AgencyInfo?.AgencyEmail || '',
    ...props.values,
    ...props.response,
    FederalTaxId: props.response?.result?.entityinfo?.AgencyInfo?.TaxID || props.values?.FederalTaxId
      || props.values?.FEIN || '',
  };

  return (
    <div className={styles.wrapper}>
      <FormBuilder
        elements={elements}
        initialValues={initialValues}
        onNext={props.onNext}
        schemaShape={schemaShape}
        updateValues={props.updateValues}
        step={props.step}
        setStep={props.setStep}
      />
    </div>
  );
};

export default Step1;
